import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AiChatAssistantMessage } from '@/components/@IntranetLibrary/AiChatView';
import { useSnackbar } from '@/hooks/global-tools';
import useApprovalChat from '@/hooks/approval/useApprovalChat';
import useApprovalDecision, {
  getApprovalDecisionErrorMessage,
  type ApprovalDecisionAction,
} from '@/hooks/approval/useApprovalDecision';
import { metaOf } from '@/utils/approval/approvalChat';
import ChatView from './ChatView';

export type AiAssistantPanelProps = {
  documentId: string;
  isMobile: boolean;
  onClose: () => void;
  /** 현재 결재 차례인 사용자인지 — 반려 제안 카드의 버튼 노출 조건 */
  canDecide: boolean;
  /** 반려 성공 후 호스트 후처리(다음 결재 문서 열기 등). DocumentActions 의 onDecided 와 같은 콜백 */
  onDecided?: (action: ApprovalDecisionAction) => void;
  /** 이 문서에 참조인을 추가할 수 있는지 — 「○○에게 전달해」 카드 (PORTAL-15736) */
  canShare?: boolean;
  /** 전달(참조인 추가) 뒤 결재 화면 캐시 갱신 */
  onShared?: () => void | Promise<void>;
};

/**
 * 결재 AI 어시스턴트 패널 — 결재용 데이터 훅(useApprovalChat)과 반려 처리(useApprovalDecision)를
 * 목업 룩의 ChatView 에 연결한다. 패널은 열려 있는 동안만 마운트되고, 열 때마다 서버 이력을 복원한다.
 */
const AiAssistantPanel = ({
  documentId,
  isMobile,
  onClose,
  canDecide,
  onDecided,
  canShare = false,
  onShared,
}: AiAssistantPanelProps) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const {
    messages,
    isSending,
    isInitializing,
    send,
    loadMore,
    hasMore,
    isLoadingMore,
    answerSuggestion,
  } = useApprovalChat(documentId);
  const { decide, isSubmitting } = useApprovalDecision();
  const [decidingId, setDecidingId] = useState<string | null>(null);

  const suggestions = [
    t('핵심을 요약해줘'),
    t('숫자와 내용의 불일치가 없는지 검토해줘'),
    t('반려할 만한 사유가 있는지 검토해줘'),
  ];

  /**
   * 「네, 반려 처리」/「네, 승인 처리」 — 제안 코멘트를 의견으로 실어 기존 반려(reject)·결재(confirm) API 호출.
   * 성공하면 결재 화면 캐시는 훅이 무효화하고, 대화에는 처리 결과 말풍선을 남긴다. 실패(차례 아님·잠금 등)는 토스트로.
   */
  const handleAccept = useCallback(
    async (m: AiChatAssistantMessage, options?: { viaChat?: boolean }) => {
      const meta = metaOf(m);
      if (!meta?.suggestion || isSubmitting) return;
      const isApprove = meta.suggestion.action === 'approve';
      const action: ApprovalDecisionAction = isApprove ? 'confirm' : 'reject';
      const comment = meta.suggestion.comment.trim();
      setDecidingId(m.id);
      try {
        await decide(action, documentId, comment);
        const resultText = comment
          ? `${
              isApprove
                ? t('코멘트를 입력하고 승인 처리했습니다.')
                : t('코멘트를 입력하고 반려 처리했습니다.')
            }\n\n${t('입력된 코멘트')}: "${comment}"`
          : t('승인 처리했습니다.');
        answerSuggestion(m.id, 'accepted', {
          // 채팅으로 답해 실행한 경우는 사용자의 말("ㅇㅇ")이 이미 대화에 있다
          user: options?.viaChat
            ? undefined
            : isApprove
            ? t('네, 승인 처리해줘')
            : t('네, 반려 처리해줘'),
          assistant: resultText,
          doneLabel: isApprove ? t('승인 완료') : t('반려 완료'),
        });
        showSuccess({
          message: isApprove
            ? t('결재가 완료되었습니다.')
            : t('결재가 반려되었습니다.'),
        });
        onDecided?.(action);
      } catch (error) {
        showError({
          message: t(getApprovalDecisionErrorMessage(error, action)),
        });
      } finally {
        setDecidingId(null);
      }
    },
    [
      answerSuggestion,
      decide,
      documentId,
      isSubmitting,
      onDecided,
      showError,
      showSuccess,
      t,
    ],
  );

  const handleDecline = useCallback(
    (m: AiChatAssistantMessage) => {
      answerSuggestion(m.id, 'declined', {
        user: t('아니요'),
        assistant: t(
          '알겠습니다. 결재 처리는 진행하지 않았습니다. 다른 확인이 필요하면 말씀해 주세요.',
        ),
      });
    },
    [answerSuggestion, t],
  );

  return (
    <ChatView
      isMobile={isMobile}
      messages={messages}
      isSending={isSending}
      isInitializing={isInitializing}
      suggestions={suggestions}
      onSend={send}
      onClose={onClose}
      loadMore={loadMore}
      hasMore={hasMore}
      isLoadingMore={isLoadingMore}
      canDecide={canDecide}
      decidingId={decidingId}
      onAccept={(m, options) => void handleAccept(m, options)}
      onDecline={handleDecline}
      documentId={documentId}
      canShare={canShare}
      onShared={onShared}
    />
  );
};

export default AiAssistantPanel;
